import {inject, Inject, Injectable, signal} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {WinOlsCustomersModel} from "../models/WinOlsCustomers-Model";
import {SortDirectionValuesModel} from "../models/SortDirectionValues-model";
import {MatDialog} from "@angular/material/dialog";
import {NotificationService} from "./Notification.service";
import {DeleteDialogComponent} from "../helpers/delete-dialog/delete-dialog.component";

@Injectable({
    providedIn: 'root'
})
export class WinOlsCustomersService {
    private url = `${this.API_URL}`;

    public winOlsCustomers = signal<WinOlsCustomersModel[]>([]);
    public customerIndex = signal<number | null>(null);
    public editInputRow = signal(false);
    public newInputRow = signal<WinOlsCustomersModel>({} as WinOlsCustomersModel);
    public isLoading = signal<boolean>(false);
    public isAddingNewCustomer = signal<boolean>(false);
    public notificationService = inject(NotificationService);
    dialog = inject(MatDialog);

    public pageNumber = signal<number>(0);
    public pageSize = signal<number>(50);
    public sortDirectionValues = signal<SortDirectionValuesModel>({active: "id", direction: "asc"});

    public searchQuery = signal<string>('');
    public isSearchMode = signal<boolean>(false);
    public searchResults = signal<WinOlsCustomersModel[]>([]);
    public searchPageNumber = signal<number>(0);
    public totalSearchPages = signal<number>(0);
    public totalSearchElements = signal<number>(0);

    constructor(@Inject('API_URL') private API_URL: string, private httpClient: HttpClient) {
    }

    triggerAllWinOlsCustomers() {
        return this.httpClient.get<WinOlsCustomersModel[]>(`${this.url}/winOlsCustomers/all`, {
            params: {
                pageNumber: this.pageNumber(),
                pageSize: this.pageSize(),
                sortDirection: this.sortDirectionValues().direction
            }
        });
    }

    triggerSearchWinOlsCustomers() {return this.httpClient.get<{ content: WinOlsCustomersModel[],
            totalPages: number,
            totalElements: number,
            size: number,
            number: number
        }>(`${this.url}/winOlsCustomers/search`, {
            params: {
                search: this.searchQuery(),
                pageNumber: this.searchPageNumber(),
                pageSize: this.pageSize()
            }
        });
    }

    searchWinOlsCustomers(query: string) {
        this.searchQuery.set(query.trim());

        if (this.searchQuery().length >= 3) {
            this.isSearchMode.set(true);
            this.searchPageNumber.set(0);
            this.isLoading.set(true);

            this.triggerSearchWinOlsCustomers().subscribe({
                next: (response) => {
                    this.searchResults.set(response.content);
                    this.totalSearchPages.set(response.totalPages);
                    this.totalSearchElements.set(response.totalElements);
                    this.isLoading.set(false);
                },
                error: (error) => {
                    this.isLoading.set(false);
                    this.notificationService.showErrorMessage("Error searching customers", "Error");
                    console.error('Search error:', error);
                }
            });
        }

        if (this.searchQuery().length === 0) {
            this.clearSearch();
        }
    }


    loadMoreSearchResults() {
        if (this.searchPageNumber() >= this.totalSearchPages() - 1) {
            return;
        }
        this.searchPageNumber.update(page => page + 1);
        this.isLoading.set(true);

        this.triggerSearchWinOlsCustomers().subscribe({
            next: (response) => {
                this.searchResults.update(current => [...current, ...response.content]);
                this.isLoading.set(false);
            },
            error: (error) => {
                this.isLoading.set(false);
                this.notificationService.showErrorMessage("Error loading more search results", "Error");
            }
        });
    }

    clearSearch() {
        this.searchQuery.set('');
        this.isSearchMode.set(false);
        this.searchResults.set([]);
        this.searchPageNumber.set(0);
        this.totalSearchPages.set(0);
        this.totalSearchElements.set(0);
        this.getWinOlsCustomersTable();
    }


    getCurrentCustomers(): WinOlsCustomersModel[] {
        return this.isSearchMode() ? this.searchResults() : this.winOlsCustomers();
    }

    getWinOlsCustomersTable() {
        this.isLoading.set(true);
        this.triggerAllWinOlsCustomers().subscribe({
            next: (response) => {
                this.winOlsCustomers.set(response["content"] as WinOlsCustomersModel[]);
                this.isLoading.set(false);
            },
            error: (error) => {
                this.isLoading.set(false);
                this.notificationService.showErrorMessage("Error loading customers", "Error");
            }
        });
    }

    getWinOlsCustomersOnScroll() {
        console.log('getWinOlsCustomersOnScroll called for page:', this.pageNumber());

        this.triggerAllWinOlsCustomers().subscribe({
            next: (response) => {
                console.log('Scroll response:', response);
                console.log('Current customers count before:', this.winOlsCustomers().length);
                this.winOlsCustomers.update((customers) => [...customers, ...response["content"]]);

                console.log('Current customers count after:', this.winOlsCustomers().length);
                this.isLoading.set(false);
            },
            error: (error) => {
                console.error('Error in getWinOlsCustomersOnScroll:', error);
                this.isLoading.set(false);
                this.notificationService.showErrorMessage("Error loading more customers", "Error");
            }
        });
    }

    onScroll() {
        if (this.isLoading()) {
            console.log('Already loading, skipping...');
            return;
        }

        if (this.isSearchMode()) {
            this.loadMoreSearchResults();
            return;
        }
        console.log('Loading page:', this.pageNumber() + 1);
        this.isLoading.set(true);
        this.increasePageNumber();
        this.getWinOlsCustomersOnScroll();
    }


    increasePageNumber() {
        this.pageNumber.update((prevNum) => prevNum + 1);
    }


    changeSortDirection(sortDirection: SortDirectionValuesModel, tableElement?: HTMLElement) {
        try {
            this.pageNumber.set(0);
            this.sortDirectionValues.set(sortDirection);
            if (tableElement) {
                tableElement.scrollTop = 0;
            }

            this.triggerAllWinOlsCustomers().subscribe({
                next: (response) => {
                    if (response) {
                        this.winOlsCustomers.set([...response["content"]]);
                    }
                },
                error: (error) => {
                    if (error.status === 409) {
                        this.notificationService.showErrorMessage("This attribute does not exist in this table", "Error");
                    } else {
                        this.notificationService.showErrorMessage("Something went wrong", "Error Occurred");
                    }
                    this.resetSortDirection();
                }
            });
        } catch (error) {
            this.notificationService.showErrorMessage("Error occurred while sorting", "Error");
        }
    }

    resetSortDirection() {
        this.sortDirectionValues.set({active: "id", direction: "asc"});
    }

    resetPageNumber() {
        this.pageNumber.set(0);
    }

    onRefreshTable() {
        this.pageNumber.set(0);
        this.pageSize.set(50);
        this.sortDirectionValues.set({active: "id", direction: "asc"});
        this.winOlsCustomers.set([]);
        this.clearSearch();
        this.getWinOlsCustomersTable();
    }

    triggerOnScroll() {
        this.increasePageNumber();
        this.triggerAllWinOlsCustomers().subscribe(response => {
            this.winOlsCustomers.update((customers) => [...customers, ...response["content"]]);
        });
    }

    addWinOlsCustomer() {
        this.isAddingNewCustomer.set(true);
        this.httpClient.post<WinOlsCustomersModel>(`${this.url}/winOlsCustomers`, {
            name: this.newInputRow().name,
            email: this.newInputRow().email,
            customerNumber: this.newInputRow().customerNumber,
        }, {
            observe: "response"
        }).subscribe({
            next: response => {
                if (response.status === 201) {
                    this.onRefreshTable();
                    this.isAddingNewCustomer.set(false);
                    this.notificationService.showSuccessMessage("Successfully added", 'Successfully added');
                }
                this.resetNewInputRow();
                this.editInputRow.set(false);
            },
            error: err => {
                this.isAddingNewCustomer.set(false);
                this.handleError(err);
            }
        });
    }

    resetNewInputRow() {
        this.newInputRow.set({} as WinOlsCustomersModel);
    }

    openNewRow() {
        this.editInputRow.set(!this.editInputRow());
        this.resetNewInputRow();
    }

    resetComponentState() {
        this.editInputRow.set(false);
        this.customerIndex.set(null);
        this.resetNewInputRow();
        this.isAddingNewCustomer.set(false);
        this.clearSearch();
    }

    updateWinOlsCustomer(newCustomer: WinOlsCustomersModel) {
        this.isAddingNewCustomer.set(true);
        this.httpClient.put<WinOlsCustomersModel>(`${this.url}/winOlsCustomers/${newCustomer.id}`, {
            name: newCustomer.name,
            email: newCustomer.email,
            customerNumber: newCustomer.customerNumber,
            emailSubscribed: newCustomer.emailSubscribed
        }, {
            observe: "response"
        }).subscribe({
            next: response => {
                if (response.status === 200) {
                    this.winOlsCustomers.update((customers) =>
                        customers.map((prevCustomer) =>
                            prevCustomer.id === newCustomer.id ? response.body : prevCustomer
                        )
                    );
                    if (this.isSearchMode()) {
                        this.searchResults.update((customers) =>
                            customers.map((prevCustomer) =>
                                prevCustomer.id === newCustomer.id ? response.body : prevCustomer
                            )
                        );
                    }
                    this.notificationService.showSuccessMessage("Successfully updated", "Successfully updated");
                    this.customerIndex.set(null);
                }
                this.isAddingNewCustomer.set(false);
            },
            error: err => {
                this.isAddingNewCustomer.set(false);
                this.handleError(err);
            }
        });
    }

    updateCustomerSubscription(newCustomer: WinOlsCustomersModel) {
        this.isAddingNewCustomer.set(true);

        this.httpClient.put<WinOlsCustomersModel>(
            `${this.url}/winOlsCustomers/winOls/update-subscription`, {
                email: newCustomer.email,
                subscribed: newCustomer.emailSubscribed,
            },
            { observe: "response" }
        ).subscribe({
            next: response => {
                if (response.status === 200) {
                    this.winOlsCustomers.update(customers =>
                        customers.map(c =>
                            c.email === newCustomer.email
                                ? { ...c, emailSubscribed: newCustomer.emailSubscribed }
                                : c
                        )
                    );

                    if (this.isSearchMode()) {
                        this.searchResults.update(customers =>
                            customers.map(c =>
                                c.email === newCustomer.email
                                    ? { ...c, emailSubscribed: newCustomer.emailSubscribed }
                                    : c
                            )
                        );
                    }

                    this.notificationService.showSuccessMessage(
                        newCustomer.emailSubscribed ? "Subscribe enabled" : "Subscribe disabled",
                        "Success"
                    );
                }
                this.isAddingNewCustomer.set(false);
            },
            error: (err) => {
                this.isAddingNewCustomer.set(false);
                this.notificationService.showErrorMessage("Error updating subscription", "Error");
                console.error('Subscription update error:', err);
            }
        });
    }





    deleteWinOlsCustomer(customer: WinOlsCustomersModel) {
        const dialogRef = this.dialog.open(DeleteDialogComponent, {
            width: "350px",
            data: "Are you sure you want to delete this customer?"
        })
        dialogRef.afterClosed().subscribe(confirmed => {
            if (confirmed) {
                this.deleteWinOlsCustomerRequest(customer.id);
            }
        })
    }

    deleteWinOlsCustomerRequest(id: number) {
        this.httpClient.delete(`${this.url}/winOlsCustomers/${id}`, {observe: "response"}).subscribe({
            next: response => {
                if (response.status === 200) {
                    this.winOlsCustomers.update((customers) => customers.filter((customer) => customer.id != id));

                    if (this.isSearchMode()) {
                        this.searchResults.update((customers) => customers.filter((customer) => customer.id != id));
                    }

                    this.notificationService.showSuccessMessage("Successfully deleted", `Successfully deleted customer ${id}`);
                }
            },
            error: (error) => {
                this.notificationService.showErrorMessage("Error deleting customer", "Error");
            }
        });
    }

    getWinOlsCustomerById(id: number) {
        return this.httpClient.get<WinOlsCustomersModel>(`${this.url}/winOlsCustomers/${id}`)
    }

    private handleError(err: any) {
        if (err.status === 400) {
            const errors = err?.error?.errors;
            if (errors && typeof errors === 'object') {
                Object.entries(errors).forEach(([field, message]) => {
                    if (field === 'name') {
                        this.notificationService.showErrorMessage(`Name: ${message}`, 'Error');
                    } else if (field === 'email') {
                        this.notificationService.showErrorMessage(`Email: ${message}`, 'Error');
                    } else if (field === 'customerNumber') {
                        this.notificationService.showErrorMessage(`Customer Number: ${message}`, 'Error');
                    }
                });
            } else {
                this.notificationService.showErrorMessage("Invalid input data", "Validation Error");
            }
        } else if (err.status === 409) {
            const backendMessage = err?.error?.message;
            if (backendMessage) {
                this.notificationService.showErrorMessage(backendMessage, "Error");
            } else {
                this.notificationService.showErrorMessage("Customer already exists", "Error");
            }
        } else if (err.status === 404) {
            this.notificationService.showErrorMessage("Customer not found", "Not Found");
        } else {
            this.notificationService.showErrorMessage("Unexpected error occurred", "Error");
        }
    }
}
